/**
 * Freight › This month (TASK-0258, DROP-2b): billed against est. by drop-off location and by vendor, freight as a
 * share of the goods received and of the month's sales (owner decision 8: freight in the overhead picture), what
 * is not placed and what is not on an order. Read-only; the Reports tab has the same numbers over any days.
 */
import { Link } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { addStoreDays } from '@kk-pos/shared/drop-off';
import { monthStartOf } from '@kk-pos/shared/landed-cost';
import { errorMessage } from '@/lib/api/client';
import { money, percent } from '@/lib/format';
import { Button } from '@/ui/Button';
import { ErrorState, Panel, Skeleton, Stat, StatRow } from '@/ui/Panel';
import { useFreightReport } from './api';
import { localToday } from './model';
import type { FreightRow } from './types';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** The calendar month `offset` months from today's, as days. */
export function monthWindow(
  today: string,
  offset: number
): { from: string; to: string; label: string } {
  let from = monthStartOf(today);
  for (let i = 0; i < Math.abs(offset); i += 1) {
    from =
      offset < 0
        ? monthStartOf(addStoreDays(from, -1))
        : monthStartOf(addStoreDays(from, 32));
  }
  const to = addStoreDays(monthStartOf(addStoreDays(from, 32)), -1);
  const label = `${MONTHS[Number(from.slice(5, 7)) - 1]} ${from.slice(0, 4)}`;
  return { from, to: to > today ? today : to, label };
}

export function ThisMonthPanel({
  month,
  onMonth,
}: {
  month: number;
  onMonth: (m: number) => void;
}) {
  const w = monthWindow(localToday(), month);
  const byLocation = useFreightReport(w.from, w.to, 'location');
  const byVendor = useFreightReport(w.from, w.to, 'vendor');
  const o = byLocation.data?.overhead;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Button
          size="xs"
          variant="ghost"
          icon={<ChevronLeft className="size-3.5" />}
          onClick={() => onMonth(month - 1)}
        >
          Previous month
        </Button>
        <span className="text-ink text-xs font-medium">{w.label}</span>
        <Button
          size="xs"
          variant="ghost"
          onClick={() => onMonth(month + 1)}
          disabled={month >= 0}
        >
          Next month
          <ChevronRight className="ml-1 size-3.5" />
        </Button>
        <Link
          to="/reports"
          search={{ report: 'freight', from: w.from, to: w.to } as never}
          className="text-2xs text-signal-ink ml-auto hover:underline"
        >
          Freight report
        </Link>
        <Link
          to="/reports"
          search={{ report: 'freight-margin', from: w.from, to: w.to } as never}
          className="text-2xs text-signal-ink hover:underline"
        >
          Margin with freight
        </Link>
      </div>
      {byLocation.isError ? (
        <ErrorState
          title="The month did not load"
          message={errorMessage(byLocation.error)}
          onRetry={() => void byLocation.refetch()}
        />
      ) : !o ? (
        <Skeleton className="h-24" />
      ) : (
        <StatRow className="xl:grid-cols-6">
          <Stat
            label="Freight"
            value={money(o.freightCents)}
            sub="billed + est. + not placed"
          />
          <Stat label="Billed" value={money(o.billedCents)} />
          <Stat label="est." value={money(o.estCents)} sub="not billed yet" />
          <Stat
            label="Not placed"
            value={money(o.notPlacedCents)}
            tone={o.notPlacedCents !== 0 ? 'warn' : undefined}
          />
          <Stat
            label="Freight % of goods"
            value={o.freightPctOfGoods === null ? '—' : percent(o.freightPctOfGoods)}
            sub={`goods received ${money(o.goodsCents)}`}
          />
          <Stat
            label="Freight % of sales"
            value={o.freightPctOfSales === null ? '—' : percent(o.freightPctOfSales)}
            sub={`sales ${money(o.salesCents)}`}
          />
        </StatRow>
      )}
      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        <FreightTable
          title="By drop-off location"
          rows={byLocation.data?.rows}
          pending={byLocation.isPending}
        />
        <FreightTable
          title="By vendor"
          rows={byVendor.data?.rows}
          pending={byVendor.isPending}
        />
      </div>
      <p className="text-2xs text-ink-faint">
        Freight is shown beside cost, never in it: margins elsewhere use cost. Not on an
        order: placed on a bill with no order to reach (kept, never dropped).
      </p>
    </div>
  );
}

export function FreightTable({
  title,
  rows,
  pending,
  label = 'Name',
}: {
  title: string;
  rows: FreightRow[] | undefined;
  pending: boolean;
  label?: string;
}) {
  return (
    <Panel title={title} bleed>
      {pending ? (
        <Skeleton className="h-24" />
      ) : (rows ?? []).length === 0 ? (
        <p className="text-ink-faint px-3 py-3 text-xs">No freight in these days.</p>
      ) : (
        <table className="w-full text-xs" data-testid={`freight-table-${title}`}>
          <thead>
            <tr className="border-line bg-surface-sunken text-2xs text-ink-faint border-b tracking-wider uppercase">
              <th className="px-3 py-1.5 text-left font-medium">{label}</th>
              <th className="w-24 px-3 py-1.5 text-right font-medium">Billed</th>
              <th className="w-24 px-3 py-1.5 text-right font-medium">est.</th>
              <th className="w-24 px-3 py-1.5 text-right font-medium">Not placed</th>
              <th className="w-24 px-3 py-1.5 text-right font-medium">Not on an order</th>
              <th className="w-24 px-3 py-1.5 text-right font-medium">Freight</th>
              <th className="w-20 px-3 py-1.5 text-right font-medium">% of goods</th>
            </tr>
          </thead>
          <tbody>
            {(rows ?? []).map((r) => (
              <tr key={r.key} className="border-line-soft border-b last:border-0">
                <td className="text-ink px-3 py-1.5">{r.label}</td>
                <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                  {money(r.billedCents)}
                </td>
                <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                  {money(r.estCents)}
                </td>
                <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                  {money(r.notPlacedCents)}
                </td>
                <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                  {money(r.notOnOrderCents)}
                </td>
                <td className="text-ink px-3 py-1.5 text-right font-mono tabular-nums">
                  {money(r.freightCents)}
                </td>
                <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                  {r.freightPctOfGoods === null ? '—' : percent(r.freightPctOfGoods)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}
